import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Heart, Pin, Flag, Share2, RefreshCw, Loader2, Trash2, Pencil, Star, UserX } from 'lucide-react';
import { AlbumOverlayConfig, Photo } from '../../types';
import { compositePhotoToObjectUrl } from '../../lib/clientCompositor';
import styles from './AlbumPhotoCell.module.css';

// Persists across component remounts so already-seen images never replay the
// fade-in animation when the user scrolls back up.
const loadedUrlCache = new Set<string>();

export interface AlbumPhotoCellProps {
  photo: Photo;
  /** Index in the full (unfiltered) photos array — passed to onView. */
  photoIndex: number;
  isSelected: boolean;
  selectMode: boolean;
  /** Called with photoId (and shiftKey, for range-select) when tapped in select mode. */
  onSelect: (photoId: string, shiftKey?: boolean) => void;
  /** Called with photoIndex when cell is tapped in view mode. */
  onView: (photoIndex: number) => void;
  likeCount?: number;
  isLikedByMe?: boolean;
  onToggleLike?: (photoId: string, e: React.MouseEvent) => void;
  onLikeCountTap?: (photoId: string, e: React.MouseEvent) => void;
  isPinned?: boolean;
  /** Creator-only: the ZIP builder couldn't read this photo's file (corrupt). */
  onTogglePin?: (photoId: string, e: React.MouseEvent) => void;
  onReport?: (photoId: string, e: React.MouseEvent) => void;
  onShare?: (photoId: string, e: React.MouseEvent) => void;
  useClientCompositor?: boolean;
  overlayConfigs?: AlbumOverlayConfig[];
  isSyncedToGooglePhotos?: boolean;
  isGpSyncFailed?: boolean;
  isGpSyncRetrying?: boolean;
  onRetryGpSync?: (photoId: string) => void;
  /** Called when the select badge is clicked while NOT in select mode — enter select mode + select this photo. */
  onBadgeClick?: (photoId: string) => void;
  /**
   * Shows a "Not me" pill (My Photos tab only). Tapping it tells the backend this
   * face match is wrong — the photo is dropped from My Photos and the algorithm
   * learns to stop showing it. Omit everywhere else.
   */
  onNotMe?: (photoId: string, e: React.MouseEvent) => void;

  // ── Creator actions (event gallery) ──────────────────────────────────────
  // All optional; each renders its control only when the matching callback is
  // provided. Used by the creator gallery, hidden everywhere else.
  /** Shows an edit (pencil) button that opens the editor. */
  onEdit?: (photoId: string, e: React.MouseEvent) => void;
  /** Shows a delete (trash) button. */
  onDelete?: (photoId: string, e: React.MouseEvent) => void;
  /** Shows a persistent highlight star badge (and toggles its active state). */
  isHighlight?: boolean;
  /** Shows a highlight (star) toggle button. */
  onToggleHighlight?: (photoId: string, e: React.MouseEvent) => void;
  /** Shows a deleting spinner overlay and suppresses hover controls. */
  isDeleting?: boolean;
}

function AlbumPhotoCellBase({
  photo,
  photoIndex,
  isSelected,
  selectMode,
  onSelect,
  onView,
  likeCount,
  isLikedByMe,
  onToggleLike,
  onLikeCountTap,
  isPinned,
  onTogglePin,
  onReport,
  onShare,
  useClientCompositor = false,
  overlayConfigs,
  isSyncedToGooglePhotos = false,
  isGpSyncFailed = false,
  isGpSyncRetrying = false,
  onRetryGpSync,
  onBadgeClick,
  onNotMe,
  onEdit,
  onDelete,
  isHighlight = false,
  onToggleHighlight,
  isDeleting = false,
}: AlbumPhotoCellProps) {
  const baseThumbnailUrl = photo.processedThumbnailUrl ?? photo.thumbnailUrl ?? '';

  const cellRef = useRef<HTMLButtonElement>(null);
  const [compositedUrl, setCompositedUrl] = useState<string | null>(null);
  const compositorEnabled =
    useClientCompositor &&
    !!overlayConfigs &&
    overlayConfigs.length > 0 &&
    !photo.processedThumbnailUrl &&
    !!photo.thumbnailUrl;

  useEffect(() => {
    if (!compositorEnabled) return;
    const el = cellRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    let cancelled = false;
    let generatedUrl: string | null = null;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        compositePhotoToObjectUrl({
          baseUrl: photo.thumbnailUrl!,
          overlayConfigs: overlayConfigs!,
          maxDimension: 800,
        })
          .then((url) => {
            if (cancelled) { URL.revokeObjectURL(url); return; }
            generatedUrl = url;
            setCompositedUrl(url);
          })
          .catch((err) => {
            console.warn('[AlbumPhotoCell] Client-side composite failed:', err);
          });
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => {
      cancelled = true;
      obs.disconnect();
      if (generatedUrl) URL.revokeObjectURL(generatedUrl);
    };
  }, [compositorEnabled, photo.thumbnailUrl, overlayConfigs]);

  const thumbnailUrl = compositedUrl ?? baseThumbnailUrl;

  const [imgLoaded, setImgLoaded] = useState(() => loadedUrlCache.has(thumbnailUrl));
  const [isSharing, setIsSharing] = useState(false);
  const sharingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setImgLoaded((prev) => {
      if (prev) return prev;
      return loadedUrlCache.has(thumbnailUrl);
    });
  }, [thumbnailUrl]);

  const handleImageLoad = useCallback(() => {
    loadedUrlCache.add(thumbnailUrl);
    setImgLoaded(true);
  }, [thumbnailUrl]);

  // 'processing' = thumbnail is up but the original is still compressing, so it
  // can't be opened yet. Show a "preparing" pill and block opening until ready.
  const isPreparing = photo.status === 'processing';

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (selectMode) { onSelect(photo._id, e.shiftKey); return; }
    if (isPreparing) return; // opening would hit a not-yet-available original
    onView(photoIndex);
  }, [selectMode, onSelect, onView, photo._id, photoIndex, isPreparing]);

  const handleBadgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectMode) {
      onSelect(photo._id, e.shiftKey);
    } else {
      onBadgeClick?.(photo._id);
    }
  }, [selectMode, onSelect, onBadgeClick, photo._id]);

  const handleShareClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onShare || isSharing) return;
    setIsSharing(true);
    onShare(photo._id, e);
    if (sharingTimerRef.current) clearTimeout(sharingTimerRef.current);
    sharingTimerRef.current = setTimeout(() => setIsSharing(false), 1500);
  }, [onShare, photo._id, isSharing]);

  const ariaLabel = selectMode
    ? `${isSelected ? 'Deselect' : 'Select'} ${photo.originalFilename}`
    : isPreparing
      ? `${photo.originalFilename} — preparing, not ready to open yet`
      : `View ${photo.originalFilename}`;

  return (
    <motion.button
      ref={cellRef}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={selectMode ? isSelected : undefined}
      aria-disabled={isPreparing && !selectMode ? true : undefined}
      data-select-mode={selectMode ? 'true' : 'false'}
      data-preparing={isPreparing ? 'true' : 'false'}
      className={[
        styles['cell'],
        selectMode && isSelected ? styles['cell-selected'] : '',
      ].filter(Boolean).join(' ')}
      // whileHover removed — fires on touchstart on mobile and causes scroll jank.
      // Lift effect is now CSS :hover only (GPU-composited transform).
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'tween', duration: 0.08 }}
    >
      <div
        className={styles['inner']}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className={`${styles['image-wrapper']} ${imgLoaded ? styles['image-loaded'] : styles['image-loading']}`}>
          <img
            loading={imgLoaded ? 'eager' : 'lazy'}
            src={thumbnailUrl}
            alt={photo.originalFilename}
            onLoad={handleImageLoad}
            className={styles['image']}
          />
        </div>
        <div className={styles['gradient-overlay']} />
      </div>

      {/* Always visible pin indicator/toggle if pinned */}
      {isPinned && !selectMode && (
        onTogglePin ? (
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin(photo._id, e); }}
            aria-label="Unpin photo"
            className={styles['pin-toggle']}
          >
            <span className={`${styles['pin-icon-wrap']} ${styles['pin-icon-wrap-active']}`}>
              <Pin className={`${styles['icon-sm']} fill-white text-white`} strokeWidth={1.5} />
            </span>
          </button>
        ) : (
          <div className={styles['pin-toggle']} style={{ cursor: 'default' }}>
            <span className={`${styles['pin-icon-wrap']} ${styles['pin-icon-wrap-active']}`}>
              <Pin className={`${styles['icon-sm']} fill-white text-white`} strokeWidth={1.5} />
            </span>
          </div>
        )
      )}

      <div
        role="button"
        aria-label={selectMode ? (isSelected ? 'Deselect photo' : 'Select photo') : 'Select photo'}
        onClick={handleBadgeClick}
        className={styles['select-btn']}
      >
        <span className={[
          styles['select-icon-wrap'],
          isSelected ? styles['select-icon-wrap-checked'] : styles['select-icon-wrap-unchecked'],
        ].join(' ')}>
          <Check className={styles['icon-check-white']} strokeWidth={3} />
        </span>
      </div>

      {/* "Not me" pill — My Photos tab only. Always visible (works on touch,
          where hover controls never appear) so a wrong face match is one tap to
          remove. Hidden in select mode. */}
      {onNotMe && !selectMode && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNotMe(photo._id, e); }}
          aria-label="Not me — remove this photo from my photos"
          className={styles['not-me-btn']}
        >
          <UserX className={styles['not-me-icon']} strokeWidth={2} />
          <span className={styles['not-me-text']}>Not me</span>
        </button>
      )}

      {/* Google Photos synced badge — top-left, hidden in select mode. */}
      {isSyncedToGooglePhotos && !selectMode && (
        <div className={styles['gp-synced-badge']}>
          <span className={styles['gp-synced-icon-wrap']}>
            <img
              src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
              width={16}
              height={16}
              alt="Synced to Google Photos"
              draggable={false}
            />
          </span>
        </div>
      )}

      {/* Google Photos sync failed — retry overlay strip at the bottom */}
      {isGpSyncFailed && !selectMode && (
        <button
          onClick={(e) => { e.stopPropagation(); onRetryGpSync?.(photo._id); }}
          aria-label="Upload to Google Photos failed — tap to retry"
          className={styles['gp-retry-overlay']}
          disabled={isGpSyncRetrying}
        >
          <motion.span
            style={{ display: 'inline-flex' }}
            animate={isGpSyncRetrying ? { rotate: 360 } : { rotate: 0 }}
            transition={isGpSyncRetrying ? { duration: 0.9, repeat: Infinity, ease: 'linear' } : {}}
          >
            <RefreshCw className={styles['gp-retry-icon']} strokeWidth={2} />
          </motion.span>
          <span className={styles['gp-retry-text']}>
            {isGpSyncRetrying ? 'Retrying…' : 'Upload failed · Retry'}
          </span>
        </button>
      )}


      {/* Persistent highlight star badge (creator gallery) — hidden on hover so
          the highlight toggle button can take its place. */}
      {isHighlight && !selectMode && (
        <div className={styles['highlight-badge']} aria-label="Highlighted" title="Highlighted">
          <Star className={styles['icon-star']} fill="currentColor" strokeWidth={1.5} />
        </div>
      )}

      {/* Creator actions — delete (top-right) + highlight/edit (bottom-left).
          Delete/highlight stay hidden while preparing (need the not-yet-ready
          processed original), but Edit is allowed — it can work off the raw
          S3 upload (see serveOriginal) even before processing finishes. */}
      {!selectMode && !isDeleting && (onEdit || (!isPreparing && (onDelete || onToggleHighlight))) && (
        <div className={styles['hover-controls']}>
          {onDelete && !isPreparing && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(photo._id, e); }}
              aria-label="Delete photo"
              className={styles['creator-delete-btn']}
            >
              <span className={styles['creator-delete-icon-wrap']}>
                <Trash2 className={styles['icon-action']} strokeWidth={1.5} />
              </span>
            </button>
          )}

          {(onEdit || (onToggleHighlight && !isPreparing)) && (
            <div className={styles['bottom-left-actions']}>
              {onToggleHighlight && !isPreparing && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleHighlight(photo._id, e); }}
                  aria-label={isHighlight ? 'Remove highlight' : 'Mark as highlight'}
                  aria-pressed={isHighlight}
                  className={styles['action-btn']}
                >
                  <span className={[
                    styles['action-icon-wrap'],
                    isHighlight ? styles['action-icon-wrap-highlight'] : '',
                  ].join(' ')}>
                    <Star className={styles['icon-action']} fill={isHighlight ? 'currentColor' : 'none'} strokeWidth={1.5} />
                  </span>
                </button>
              )}
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(photo._id, e); }}
                  aria-label="Edit photo"
                  className={styles['action-btn']}
                >
                  <span className={[styles['action-icon-wrap'], styles['action-icon-wrap-edit']].join(' ')}>
                    <Pencil className={styles['icon-action']} strokeWidth={1.75} />
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions only visible on hover (desktop) or via viewer (mobile) */}
      {!selectMode && (onTogglePin || onReport || onShare) && (
        <div className={styles['hover-controls']}>
          {onTogglePin && !isPinned && (
            <button
              onClick={(e) => { e.stopPropagation(); onTogglePin(photo._id, e); }}
              aria-label="Pin photo"
              className={styles['pin-toggle']}
            >
              <span className={`${styles['pin-icon-wrap']} ${styles['pin-icon-wrap-inactive']}`}>
                <Pin className={`${styles['icon-sm']} fill-none text-white/80`} strokeWidth={1.5} />
              </span>
            </button>
          )}

          {/* Bottom-left actions — plain buttons with CSS :active instead of motion springs. */}
          {(onReport || onShare) && (
            <div className={styles['bottom-left-actions']}>
              {onReport && (
                <button
                  onClick={(e) => { e.stopPropagation(); onReport(photo._id, e); }}
                  aria-label="Report photo"
                  className={[styles['action-btn'], styles['action-btn-report']].join(' ')}
                >
                  <span className={styles['action-icon-wrap']}>
                    <Flag className={styles['icon-action']} strokeWidth={1.5} />
                  </span>
                </button>
              )}
              {onShare && (
                <button
                  onClick={handleShareClick}
                  aria-label="Share photo"
                  className={[styles['action-btn'], styles['action-btn-share']].join(' ')}
                >
                  <span className={[
                    styles['action-icon-wrap'],
                    isSharing ? styles['action-icon-wrap-sharing'] : '',
                  ].join(' ')}>
                    {isSharing
                      ? <Loader2 className={[styles['icon-action'], styles['icon-spin']].join(' ')} strokeWidth={1.5} />
                      : <Share2 className={styles['icon-action']} strokeWidth={1.5} />
                    }
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {onToggleLike && !selectMode && (
        <div className={styles['like-area']}>
          <span className={styles['like-pill']}>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleLike(photo._id, e); }}
              aria-label={isLikedByMe ? 'Unlike photo' : 'Like photo'}
              className={styles['like-btn']}
            >
              <Heart
                className={styles['icon-heart']}
                style={{
                  '--heart-fill': isLikedByMe ? 'var(--clay-rose)' : 'none',
                  '--heart-color': isLikedByMe ? 'var(--clay-rose)' : 'rgba(255,255,255,0.8)',
                } as React.CSSProperties}
                strokeWidth={1.5}
              />
            </button>
            {(likeCount ?? 0) > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onLikeCountTap?.(photo._id, e); }}
                aria-label={`${likeCount} like${likeCount !== 1 ? 's' : ''} — tap to see who liked`}
                className={styles['like-count-btn']}
              >
                <span className={styles['like-count-text']}>
                  {likeCount}
                </span>
              </button>
            )}
          </span>
        </div>
      )}

      {/* Preparing pill — original still compressing; opening is blocked. */}
      {isPreparing && !isDeleting && (
        <div className={styles['preparing-pill']} aria-hidden="true">
          <Loader2 className={styles['preparing-icon']} strokeWidth={2.25} />
          <span className={styles['preparing-text']}>Preparing</span>
        </div>
      )}

      {/* Deleting spinner overlay (creator gallery) */}
      {isDeleting && (
        <div className={styles['deleting-overlay']}>
          <span className={styles['deleting-spinner']} />
        </div>
      )}
    </motion.button>
  );
}

export const AlbumPhotoCell = memo(AlbumPhotoCellBase, (prev, next) => {
  return (
    prev.photo._id === next.photo._id &&
    prev.photo.thumbnailUrl === next.photo.thumbnailUrl &&
    prev.photo.processedThumbnailUrl === next.photo.processedThumbnailUrl &&
    // Re-render when the original becomes ready so the "preparing" pill clears
    // and opening is unblocked.
    prev.photo.status === next.photo.status &&
    prev.photo.originalUrl === next.photo.originalUrl &&
    prev.photoIndex === next.photoIndex &&
    prev.isSelected === next.isSelected &&
    prev.selectMode === next.selectMode &&
    prev.likeCount === next.likeCount &&
    prev.isLikedByMe === next.isLikedByMe &&
    prev.isPinned === next.isPinned &&
    prev.onSelect === next.onSelect &&
    prev.onView === next.onView &&
    prev.onToggleLike === next.onToggleLike &&
    prev.onLikeCountTap === next.onLikeCountTap &&
    prev.onTogglePin === next.onTogglePin &&
    prev.onReport === next.onReport &&
    prev.onShare === next.onShare &&
    prev.isGpSyncFailed === next.isGpSyncFailed &&
    prev.isGpSyncRetrying === next.isGpSyncRetrying &&
    prev.onRetryGpSync === next.onRetryGpSync &&
    prev.onBadgeClick === next.onBadgeClick &&
    prev.onNotMe === next.onNotMe &&
    prev.isHighlight === next.isHighlight &&
    prev.isDeleting === next.isDeleting &&
    prev.onEdit === next.onEdit &&
    prev.onDelete === next.onDelete &&
    prev.onToggleHighlight === next.onToggleHighlight
  );
});
